import React, {
  KeyboardEvent,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import { useThrottle } from "../../hooks/useThrottle";
import {
  TableViewport,
  StyledTable,
  TableHeader,
  HeaderRow,
  HeaderCell,
  TableBody,
  TableRow,
  TableCell,
  FullRowCell,
  PaginationContainer,
  ResultsCount,
  TableHorizontalScroll,
  TableScrollContainer,
} from "./DataTable.styles";
import { numberToWords } from "../../utils";

export type Column<T> = {
  key: keyof T | string;
  header: string | React.ReactNode;
  render?: (row: T) => React.ReactNode;
  width?: number | string;
  align?:
  | "left"
  | "center"
  | "right"
  | ((row: T) => "left" | "center" | "right");
};

export type DataTableProps<T> = {
  data: T[];
  columns: Column<T>[];
  total: number;
  onLoadMore?: () => void;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  rowKey?: keyof T | ((row: T) => React.Key);
  className?: string;
  loading?: boolean;
  hasMore?: boolean;
  emptyState?: React.ReactNode;
  ariaLabel?: string;
  headerActions?: React.ReactNode;
  alwaysShowResultsCount?: boolean;
};

// Helper function to get alignment value
const getAlignment = <T,>(
  column: Column<T>,
  row?: T
): "left" | "center" | "right" => {
  if (typeof column.align === "function" && row) {
    return column.align(row);
  }
  return (column.align as "left" | "center" | "right") ?? "left";
};

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  total,
  onLoadMore,
  onRowClick,
  rowClassName,
  rowKey,
  className,
  loading = false,
  hasMore = false,
  emptyState,
  ariaLabel = "Tabla de datos",
  headerActions,
  alwaysShowResultsCount = false,
}: DataTableProps<T>): React.JSX.Element {
  const isLoadingMoreRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);

  const handleRowClick = (row: T) => {
    onRowClick?.(row);
  };

  const handleRowKeyDown = (
    event: KeyboardEvent<HTMLTableRowElement>,
    row: T
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleRowClick(row);
    }
  };

  const handleScrollBase = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!event?.currentTarget) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (
        distanceFromBottom <= 100 &&
        hasMore &&
        !loading &&
        !isLoadingMoreRef.current &&
        onLoadMore
      ) {
        isLoadingMoreRef.current = true;
        onLoadMore();
        setTimeout(() => {
          isLoadingMoreRef.current = false;
        }, 500);
      }
    },
    [hasMore, loading, onLoadMore]
  );

  const throttledScrollHandler = useThrottle(handleScrollBase, 100);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!event?.currentTarget) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Actualizar el estado de scroll disponible
      setHasScroll(scrollHeight > clientHeight);

      if (distanceFromBottom <= 50) {
        handleScrollBase(event);
      } else {
        throttledScrollHandler(event);
      }
    },
    [handleScrollBase, throttledScrollHandler]
  );

  // Efecto para detectar si hay scroll disponible cuando cambian los datos
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const hasScrollableContent =
        container.scrollHeight > container.clientHeight;
      // Solo mostrar scroll si hay datos y hay contenido scrolleable
      setHasScroll(hasScrollableContent && data.length > 0);
    }
  }, [data]);

  const getCellValue = (row: T, column: Column<T>) => {
    if (column.render) return column.render(row);
    const value = row[column.key as keyof T];
    return value != null ? String(value) : "";
  };

  const getRowKey = (row: T, index: number): React.Key => {
    if (typeof rowKey === "function") return rowKey(row);
    if (typeof rowKey === "string" || typeof rowKey === "number") {
      const value = row[rowKey];
      if (typeof value === "string" || typeof value === "number") {
        return value;
      }
    }
    // Fallback if no valid rowKey is provided
    return `row-${index}`;
  };

  return (
    <TableViewport role="region" aria-label={ariaLabel} className={className}>
      <PaginationContainer>
        <ResultsCount aria-live="polite">
          {(data.length >= 0 || alwaysShowResultsCount) &&
            `${total} (${numberToWords(total)}) resultados encontrados`}
        </ResultsCount>
        {headerActions && <div>{headerActions}</div>}
      </PaginationContainer>

      <TableHorizontalScroll aria-label="Scroll horizontal de la tabla">
        <TableScrollContainer
          ref={scrollContainerRef}
          onScroll={handleScroll}
          data-table-body-container
          data-has-scroll={hasScroll}
          data-has-data={data.length > 0}
        >
          <StyledTable role="table" data-has-data={data.length > 0}>
            <TableHeader>
              <HeaderRow>
                {columns.map((column, index) => (
                  <HeaderCell
                    key={`header-${index}`}
                    style={{
                      width: column.width,
                      textAlign: getAlignment(column),
                    }}
                    scope="col"
                  >
                    {column.header}
                  </HeaderCell>
                ))}
              </HeaderRow>
            </TableHeader>

            <TableBody>
              {data.length === 0 && !loading ? (
                <TableRow>
                  <FullRowCell colSpan={columns.length}>
                    {emptyState || <div>No hay datos disponibles</div>}
                  </FullRowCell>
                </TableRow>
              ) : (
                data.map((row, rowIndex) => {
                  const stableKey = getRowKey(row, rowIndex);
                  return (
                    <TableRow
                      key={stableKey}
                      className={rowClassName?.(row)}
                      tabIndex={onRowClick ? 0 : -1}
                      onClick={() => handleRowClick(row)}
                      onKeyDown={(e) => handleRowKeyDown(e, row)}
                      data-clickable={!!onRowClick}
                    >
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={`cell-${stableKey}-${colIndex}`}
                          style={{
                            width: column.width,
                            textAlign: getAlignment(column, row),
                          }}
                        >
                          {getCellValue(row, column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </StyledTable>
        </TableScrollContainer>
      </TableHorizontalScroll>
    </TableViewport>
  );
}
